import CheckBox from 'devextreme-react/check-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import TextBox from 'devextreme-react/text-box';
import { useMemo, useState } from 'react';
import type { UserGroupMembership } from '../../../../api/clientAccountsUsersAdmin';

type HvgClientGroupAccessPanelProps = {
  groupOptions: UserGroupMembership[];
  selectedGroupIds: string[];
  loading: boolean;
  onChange: (groupIds: string[]) => void;
  intro?: string;
  readOnly?: boolean;
};

export function HvgClientGroupAccessPanel({
  groupOptions,
  selectedGroupIds,
  loading,
  onChange,
  intro = 'Choose the security groups that control this user’s application access and SPR roles.',
  readOnly = false
}: HvgClientGroupAccessPanelProps) {
  const [accessSearch, setAccessSearch] = useState('');
  const [showAssignedOnly, setShowAssignedOnly] = useState(false);

  const visibleGroups = useMemo(() => {
    const term = accessSearch.trim().toLocaleLowerCase();
    return groupOptions.filter((group) => {
      if (showAssignedOnly && !selectedGroupIds.includes(group.groupId)) return false;
      if (!term) return true;
      return (
        group.displayName.toLocaleLowerCase().includes(term) ||
        group.assignedSprRoles.some((role) => role.toLocaleLowerCase().includes(term))
      );
    });
  }, [accessSearch, groupOptions, selectedGroupIds, showAssignedOnly]);

  const setGroupSelected = (groupId: string, selected: boolean) => {
    if (readOnly) return;
    const next = selected
      ? [...new Set([...selectedGroupIds, groupId])]
      : selectedGroupIds.filter((id) => id !== groupId);
    onChange(next);
  };

  return (
    <div className="hvg-user-tab">
      <div className="hvg-user-tab__intro">
        <div>
          <div className="hvg-user-tab__title">Group access</div>
          <p>{intro}</p>
        </div>
        <span className="hvg-user-tab__status">{selectedGroupIds.length} selected</span>
      </div>

      <section className="hvg-user-form-card hvg-user-access">
        <div className="hvg-user-access__toolbar">
          <TextBox
            value={accessSearch}
            mode="search"
            placeholder="Search groups or roles…"
            showClearButton
            width="100%"
            onValueChanged={(e) => setAccessSearch(e.value ?? '')}
          />
          <CheckBox
            text="Assigned only"
            value={showAssignedOnly}
            onValueChanged={(e) => setShowAssignedOnly(Boolean(e.value))}
          />
        </div>

        {loading ? (
          <div className="hvg-user-access__empty">
            <LoadIndicator visible height={24} width={24} />
            <span>Loading available groups…</span>
          </div>
        ) : groupOptions.length === 0 ? (
          <div className="hvg-user-access__empty">
            <span className="dx-icon dx-icon-group" aria-hidden="true" />
            <strong>No security groups found</strong>
            <span>This tenant has no groups available for assignment.</span>
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="hvg-user-access__empty">
            <span className="dx-icon dx-icon-search" aria-hidden="true" />
            <strong>No matching groups</strong>
            <span>Change the search or turn off “Assigned only”.</span>
          </div>
        ) : (
          <div className="hvg-user-access__list">
            {visibleGroups.map((group) => {
              const selected = selectedGroupIds.includes(group.groupId);
              return (
                <div
                  className={`hvg-user-access__group${selected ? ' hvg-user-access__group--selected' : ''}`}
                  key={group.groupId}
                  role="checkbox"
                  aria-checked={selected}
                  aria-disabled={readOnly}
                  tabIndex={readOnly ? -1 : 0}
                  onClick={(event) => {
                    if (readOnly) return;
                    if ((event.target as HTMLElement).closest('.dx-checkbox')) return;
                    setGroupSelected(group.groupId, !selected);
                  }}
                  onKeyDown={(event) => {
                    if (readOnly) return;
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setGroupSelected(group.groupId, !selected);
                  }}
                >
                  <CheckBox
                    value={selected}
                    readOnly={readOnly}
                    onValueChanged={(e) => setGroupSelected(group.groupId, Boolean(e.value))}
                  />
                  <div className="hvg-user-access__group-body">
                    <strong>{group.displayName}</strong>
                    {group.assignedSprRoles.length > 0 ? (
                      <div className="hvg-user-access__roles">
                        {group.assignedSprRoles.map((role) => (
                          <span key={role}>{role}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="hvg-user-access__no-role">Security group</span>
                    )}
                  </div>
                  <span className="hvg-user-access__state">{selected ? 'Assigned' : 'Not assigned'}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
